import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Target, Beaker, AlertCircle, ChevronRight, ChevronDown, Search,
  Shield, TrendingUp, Users, Layers, Zap, ArrowLeft
} from 'lucide-react';

const SCOPE_META = {
  platform: { icon: Shield, label: 'Platform', color: '#FF6B6B', desc: 'Platform-wide objectives & immutable safety guardrails' },
  departments: { icon: Users, label: 'Departments', color: '#3EF1C7', desc: 'Department-head objectives & coordination' },
  workers: { icon: Zap, label: 'Worker Agents', color: '#FFD93D', desc: 'Per-agent KPIs & experiments' },
  flows: { icon: TrendingUp, label: 'Flows', color: '#9B59B6', desc: 'P2P, O2C, P2F, Seasonal, Drop orchestration' },
  integrations: { icon: Layers, label: 'Integrations', color: '#3498DB', desc: 'ERP, memory tiers, EDI, Polsia, Claude' },
  folders: { icon: BookOpen, label: 'Owner\'s Folders', color: '#FFA500', desc: 'Branding, Compliance, RA, Finance, Accounting' },
};

export default function PlaybooksPage() {
  const [playbooks, setPlaybooks] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch('/api/playbooks/public')
      .then(r => r.json())
      .then(data => {
        setPlaybooks(data.by_scope || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const loadPlaybook = async (scope, name) => {
    const r = await fetch(`/api/playbooks/${scope}/${name}`);
    const data = await r.json();
    setSelected({ scope, name, ...data });
  };

  const filtered = Object.entries(playbooks).reduce((acc, [scope, items]) => {
    if (!search) { acc[scope] = items; return acc; }
    const matched = items.filter(b =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.objective || '').toLowerCase().includes(search.toLowerCase())
    );
    if (matched.length) acc[scope] = matched;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to Commerce Index
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">Public Playbooks</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-3">Every agent's playbook is public.</h1>
          <p className="text-lg text-[#94A3B8] max-w-3xl">
            We don't believe agent behavior should be a black box. Every department head, every worker, every flow, every integration, every folder runs against a playbook with Objective + Constraints + KPIs + Experiments + Escalation. You can read all of them below and override them per-merchant in your CLAUDE.md.
          </p>
          <div className="mt-6 flex items-center gap-4 text-sm">
            <span className="text-[#3EF1C7]">{Object.values(playbooks).reduce((s, x) => s + x.length, 0)} playbooks total</span>
            <span className="text-[#64748B]">·</span>
            <span className="text-[#94A3B8]">7 scopes (platform → merchant)</span>
            <span className="text-[#64748B]">·</span>
            <span className="text-[#94A3B8]">Hierarchical inheritance with merchant overrides</span>
          </div>
        </div>

        <div className="mb-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <input
            type="text"
            placeholder="Search playbooks by name or objective..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#0D1628] border border-[#22304A] rounded-lg pl-10 pr-4 py-3 text-white placeholder-[#64748B] focus:border-[#3EF1C7] focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="text-[#94A3B8]">Loading playbooks...</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-6">
              {Object.entries(SCOPE_META).map(([scope, meta]) => {
                const items = filtered[scope] || [];
                if (!items.length && search) return null;
                const Icon = meta.icon;
                const isExp = expanded[scope] !== false;
                return (
                  <div key={scope} className="bg-[#0D1628] border border-[#22304A] rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [scope]: !isExp }))}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#11192A]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: meta.color + '20' }}>
                          <Icon className="w-5 h-5" style={{ color: meta.color }} />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold">{meta.label}</div>
                          <div className="text-xs text-[#64748B]">{items.length} playbook{items.length !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      {isExp ? <ChevronDown className="w-4 h-4 text-[#64748B]" /> : <ChevronRight className="w-4 h-4 text-[#64748B]" />}
                    </button>
                    {isExp && (
                      <div className="border-t border-[#22304A] divide-y divide-[#22304A]">
                        {items.map(b => (
                          <button
                            key={`${scope}/${b.name}`}
                            onClick={() => loadPlaybook(scope, b.name)}
                            className={`w-full px-5 py-3 text-left hover:bg-[#11192A] transition-colors ${
                              selected?.scope === scope && selected?.name === b.name ? 'bg-[#11192A] border-l-2 border-l-[#3EF1C7]' : ''
                            }`}
                          >
                            <div className="text-sm font-mono text-white">{b.name}</div>
                            <div className="text-xs text-[#64748B] mt-1 line-clamp-1">{b.objective}</div>
                            <div className="flex gap-3 mt-2 text-xs text-[#64748B]">
                              <span>{b.kpi_count} KPIs</span>
                              <span>·</span>
                              <span>{b.experiment_count} experiments</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-2">
              {selected ? (
                <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-8 sticky top-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#64748B] mb-1">
                        {selected.scope} / {selected.name}
                      </div>
                      <h2 className="text-2xl font-bold">{selected.name.replace(/_/g, ' ')}</h2>
                      {selected.reports_to && (
                        <div className="text-sm text-[#94A3B8] mt-1">Reports to: <span className="font-mono">{selected.reports_to}</span></div>
                      )}
                    </div>
                    <button onClick={() => setSelected(null)} className="text-[#64748B] hover:text-white">×</button>
                  </div>
                  <div className="prose prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap bg-[#0A0F1C] border border-[#22304A] rounded-lg p-5 text-sm text-[#94A3B8] font-mono">
                      {selected.raw_content}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-12 text-center">
                  <BookOpen className="w-12 h-12 text-[#3EF1C7] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">Select a playbook to read</h3>
                  <p className="text-sm text-[#94A3B8] mb-6">
                    Every Commerce Index agent runs against one of these playbooks. You can override them per-merchant in your CLAUDE.md.
                  </p>
                  <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-left">
                    <div className="flex items-start gap-2"><Target className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" /><span className="text-sm text-[#94A3B8]">Objective</span></div>
                    <div className="flex items-start gap-2"><Shield className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" /><span className="text-sm text-[#94A3B8]">Constraints</span></div>
                    <div className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" /><span className="text-sm text-[#94A3B8]">KPIs</span></div>
                    <div className="flex items-start gap-2"><Beaker className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" /><span className="text-sm text-[#94A3B8]">Experiments</span></div>
                    <div className="flex items-start gap-2 col-span-2"><AlertCircle className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" /><span className="text-sm text-[#94A3B8]">Escalation rules</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
